import { Capacitor } from "@capacitor/core";
import { useAutoUpdateStore } from "@/store/useAutoUpdateStore";
import * as semver from "semver-ts";
import { BundleInfo } from "@um/contracts";
import { logException } from "@um/ui";

// Minimum time between update checks (in milliseconds)
export const MIN_TIME_BETWEEN_CHECKS = 30 * 60 * 1000; // 30 minutes

export interface VersionComparisonResult {
  isIncompatible: boolean;
  meetsMinVersion: boolean;
}

/**
 * Compare a version against a minimum required version
 * @param version The current version to check
 * @param minVersion The minimum required version
 * @param isInitialized Whether the auto-update store is initialized
 * @param currentBuildVersion The current build version
 * @returns Object with isIncompatible and meetsMinVersion flags
 */
export function compareToMinVersion(
  version: string | undefined,
  minVersion: string | null
): VersionComparisonResult {
  if (!minVersion) {
    return { isIncompatible: false, meetsMinVersion: true };
  }

  const minV = semver.parse(minVersion);
  const curV = version ? semver.parse(version) : null;

  // A bundle is incompatible when its major and minor are different.
  // 1.2.3 and 1.2.8 are compatible, but 1.2.3 and 1.3.0 are not
  // The web is always compatible because it does not plug into the host
  const isIncompatible =
    Capacitor.isNativePlatform() &&
    !!minV &&
    !!curV &&
    (minV.major !== curV.major || minV.minor !== curV.minor);

  const meetsMinVersion =
    !minVersion || // no min version is specified so always true
    (!!version && !semver.lt(version, minVersion)) || // current bundle >= min version
    (!!minV &&
      !!curV &&
      minV.prerelease.length === 0 &&
      minV.major === curV.major &&
      minV.minor === curV.minor &&
      minV.patch === curV.patch); // technically a pre-release version (1.2.3-beta.1 < 1.2.3) but we treat them equal

  return { isIncompatible, meetsMinVersion };
}

/**
 * Check if an update check is needed based on the last check time
 * @param lastCheckTime The timestamp of the last update check
 * @returns true if an update check should be performed
 */
export function shouldCheckForUpdates(lastCheckTime: number): boolean {
  return !lastCheckTime || lastCheckTime < Date.now() - MIN_TIME_BETWEEN_CHECKS;
}

/**
 * Initialize auto-update and check for updates with optional minimum version requirement
 * @param minVersion Optional minimum version requirement from URL
 * @returns Object with update status and compatibility info
 */
export async function initializeAutoUpdate(
  minVersion?: string | null
): Promise<{
  hasUpdate: boolean;
  bundle?: BundleInfo;
  isIncompatible?: boolean;
  meetsMinVersion?: boolean;
  versionNotAvailable?: boolean;
}> {
  const { currentBundle, lastCheckTime, availableBundle, checkForUpdates } =
    useAutoUpdateStore.getState();
  try {
    let { meetsMinVersion: meetsCurrent, isIncompatible } = compareToMinVersion(
      currentBundle?.version,
      minVersion || null
    );
    let bundle =
      (minVersion && !meetsCurrent) || shouldCheckForUpdates(lastCheckTime)
        ? await checkForUpdates()
        : availableBundle;
    let { meetsMinVersion: meetsAvailible } = compareToMinVersion(
      bundle?.version,
      minVersion || null
    );

    return {
      hasUpdate: !!bundle,
      bundle: bundle || undefined,
      isIncompatible,
      meetsMinVersion: meetsCurrent || meetsAvailible,
      versionNotAvailable: !!minVersion && !meetsCurrent && !meetsAvailible,
    };
  } catch (error) {
    console.error("[auto-update-service] Update check failed:", error);
    logException(error);
    // Non-critical error, continue with app initialization
    return { hasUpdate: false };
  }
}

/**
 * Apply a pending update if one is available
 */
export async function applyPendingUpdate(): Promise<void> {
  const store = useAutoUpdateStore.getState();
  const { nextBundle, applyingUpdate } = store;

  if (nextBundle && !applyingUpdate) {
    console.log(
      "[auto-update-service] Applying pending update:",
      nextBundle.version
    );
    await store.applyUpdate();
  }
}

/**
 * Check if the app needs a store update (incompatible version)
 * @param minVersion The minimum required version
 * @returns true if a store update is required
 */
export function requiresStoreUpdate(minVersion: string | null): boolean {
  if (!minVersion || !Capacitor.isNativePlatform()) {
    return false;
  }

  const store = useAutoUpdateStore.getState();
  const { currentBundle, isInitialized, currentBuild } = store;

  const comparison = compareToMinVersion(currentBundle?.version, minVersion);

  return comparison.isIncompatible;
}
